import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { toast } from 'react-hot-toast'
import { format } from 'date-fns'
import { vendorService } from '../../../services/vendorService'
import DatePicker from '../../../components/common/DatePicker'
import { 
  Plus, 
  Trash2, 
  Upload,
  ChevronRight,
  ChevronLeft,
  FileText,
  Image as ImageIcon,
  GraduationCap,
  Building2,
  FileCheck,
  CheckCircle,
  Loader,
  User,
  Briefcase,
  X,
  Calendar,
  Award,
  Linkedin,
  Globe,
  MapPin,
  Mail,
  Phone,
  FileX
} from 'lucide-react'

const AddBenchResource = ({ isModal = false, onClose, onSuccess }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Use internal state for modal mode, URL params for page mode
  const [internalStep, setInternalStep] = useState(1)
  const urlStep = parseInt(searchParams.get('step') || '1')
  const currentStep = isModal ? internalStep : urlStep
  
  const [addingResource, setAddingResource] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [isVendorActive, setIsVendorActive] = useState(false)
  const [profileLoading, setProfileLoading] = useState(true)

  // Form state for adding new resource - multi-step form
  const [newResource, setNewResource] = useState({
    // Step 1: Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    alternatePhone: '',
    location: '',
    address: '',
    city: '',
    state: '',
    country: '',
    pincode: '',
    dateOfBirth: '',
    gender: '',
    linkedinProfile: '',
    website: '',
    
    // Step 2: Professional Details
    designation: '',
    department: '',
    totalExperience: '',
    relevantExperience: '',
    currentCTC: '',
    expectedCTC: '',
    noticePeriod: '',
    availability: '',
    currentProject: '',
    joiningDate: '',
    costperHour: '',
    summary: '',
    readyToRelocate: false,
    
    // Step 3: Educational Background
    education: [
      {
        degree: '',
        institution: '',
        fieldOfStudy: '',
        yearOfPassing: '',
        percentage: '',
        isHighest: false,
        document: null,
        documentUrl: '',
        documentName: ''
      }
    ],
    
    // Step 4: Professional Experience
    experience: [
      {
        company: '',
        designation: '',
        startDate: '',
        endDate: '',
        isCurrent: false,
        description: '',
        technologies: [],
        offerLetter: null,
        offerLetterUrl: '',
        offerLetterName: '',
        relievingLetter: null,
        relievingLetterUrl: '',
        relievingLetterName: '',
        payslip1: null,
        payslip1Url: '',
        payslip1Name: '',
        payslip2: null,
        payslip2Url: '',
        payslip2Name: '',
        payslip3: null,
        payslip3Url: '',
        payslip3Name: ''
      }
    ],
    
    // Step 5: Certifications & Skills
    certifications: [
      {
        name: '',
        issuer: '',
        issueDate: '',
        expiryDate: '',
        credentialId: '',
        credentialUrl: '',
        document: null,
        documentUrl: '',
        documentName: ''
      }
    ],
    primarySkills: [],
    secondarySkills: [],
    technologies: [],
    languages: [],
    
    // Step 6: Documents
    documents: {
      resume: null,
      resumeUrl: '',
      coverLetter: null,
      coverLetterUrl: '',
      otherDocuments: [],
      bgvDocument: null,
      bgvDocumentUrl: '',
      bgvDocumentName: '',
      selfIntro: null,
      selfIntroUrl: '',
      selfIntroName: ''
    }
  })

  const [resourceId, setResourceId] = useState(null) // Store resource_id from Step 1 API response
  const [step1Submitted, setStep1Submitted] = useState(false) // Track if Step 1 has been submitted
  const [step2Submitted, setStep2Submitted] = useState(false) // Track if Step 2 has been submitted
  const [step3Submitted, setStep3Submitted] = useState(false) // Track if Step 3 has been submitted
  const [step4Submitted, setStep4Submitted] = useState(false) // Track if Step 4 has been submitted
  const [step5Submitted, setStep5Submitted] = useState(false) // Track if Step 5 has been submitted
  const [step6Submitted, setStep6Submitted] = useState(false) // Track if Step 6 has been submitted
  
  // Track initial state of Each Step for Dirty checking (Edit Mode)
  const [stepSnapshots, setStepSnapshots] = useState({})
  
  const takeStepSnapshot = (step, data) => {
    // Create a copy without file objects for comparison
    const snapshot = JSON.stringify(data, (key, value) => {
      if (value instanceof File) return value.name + value.size + value.lastModified
      return value
    })
    setStepSnapshots(prev => ({ ...prev, [step]: snapshot }))
  }

  const isStepDirty = () => {
    if (!editId) return false
    const current = JSON.stringify(newResource, (key, value) => {
      if (value instanceof File) return value.name + value.size + value.lastModified
      return value
    })
    return stepSnapshots[currentStep] !== current
  }

  const editId = searchParams.get('id')

  useEffect(() => {
    if (editId && !resourceId) {
      setResourceId(editId)
      // When editing, we want to allow the pull effects to trigger
      setStep1Submitted(true)
      setStep2Submitted(true)
      setStep3Submitted(true)
      setStep4Submitted(true)
      setStep5Submitted(true)
      setStep6Submitted(true)
    }
  }, [editId])

  const totalSteps = 6

  // Self-intro is uploaded as a video file (no in-browser recording)

  // Local string inputs for comma-separated fields to allow free typing (spaces/commas)
  const [primarySkillsInput, setPrimarySkillsInput] = useState(newResource.primarySkills.join(', '))
  const [secondarySkillsInput, setSecondarySkillsInput] = useState(newResource.secondarySkills.join(', '))
  const [technologiesInput, setTechnologiesInput] = useState(newResource.technologies.join(', '))
  const [languagesInput, setLanguagesInput] = useState(newResource.languages.join(', '))

  useEffect(() => {
    // keep inputs in sync when newResource arrays change elsewhere (e.g., reset)
    setPrimarySkillsInput(newResource.primarySkills.join(', '))
    setSecondarySkillsInput(newResource.secondarySkills.join(', '))
    setTechnologiesInput(newResource.technologies.join(', '))
    setLanguagesInput(newResource.languages.join(', '))
  }, [newResource.primarySkills, newResource.secondarySkills, newResource.technologies, newResource.languages])

  // No live recording preview effect — self-intro uses upload only

  // Load current user
  const loadCurrentUser = async () => {
    setProfileLoading(true)
    try {
      const user = await vendorService.getCurrentUser()
      if (user) {
        setCurrentUser(user)
        const vendorId = user.vendor_id || user.vendor?.vendor_id || null
        if (vendorId) {
          sessionStorage.setItem('vendorId', JSON.stringify(vendorId))
        }
        const active = user.vendor_status === 'active' || user.status === 'active' || (user.vendor && user.vendor.status === 'active')
        setIsVendorActive(!!active)
      }
    } catch (error) {
      console.error('Error fetching current user:', error)
    } finally {
      setProfileLoading(false)
    }
  }

  useEffect(() => {
    loadCurrentUser()
  }, [])

  // Clear localStorage on mount (reload)
  useEffect(() => {
    localStorage.removeItem('newResourceForm')
  }, [])

  // Save form data to localStorage on change (without file objects to avoid quota issues)
  useEffect(() => {
    if (!profileLoading) {
      try {
        // Create a copy without file objects to reduce size
        const formDataToSave = {
          ...newResource,
          passportPhoto: null, // Don't save file objects
          education: newResource.education.map(edu => ({
            ...edu,
            document: null // Don't save file objects
          })),
          experience: newResource.experience.map(exp => ({
            ...exp,
            document: null // Don't save file objects
          })),
          certifications: (newResource.certifications || []).map(cert => ({
            ...cert,
            document: null // Don't save file objects
          })),
          documents: {
            ...newResource.documents,
            resume: null, // Don't save file objects
            coverLetter: null, // Don't save file objects
            otherDocuments: newResource.documents.otherDocuments.map(doc => ({
              ...doc,
              file: null // Don't save file objects
            }))
          }
        }
        
        const formDataString = JSON.stringify(formDataToSave)
        
        // Check if data is too large (localStorage limit is ~5-10MB)
        if (formDataString.length > 4 * 1024 * 1024) { // 4MB limit
          console.warn('Form data too large, not saving to localStorage')
          return
        }
        
        localStorage.setItem('newResourceForm', formDataString)
      } catch (error) {
        // Handle quota exceeded or other storage errors gracefully
        if (error.name === 'QuotaExceededError') {
          console.warn('LocalStorage quota exceeded, clearing old data and retrying...')
          try {
            // Clear old form data and try again
            localStorage.removeItem('newResourceForm')
            const formDataToSave = {
              firstName: newResource.firstName,
              lastName: newResource.lastName,
              email: newResource.email,
              designation: newResource.designation,
              // Only save essential fields
            }
            localStorage.setItem('newResourceForm', JSON.stringify(formDataToSave))
          } catch (retryError) {
            console.warn('Could not save form data to localStorage:', retryError)
          }
        } else {
          console.warn('Error saving form data to localStorage:', error)
        }
      }
    }
  }, [newResource, profileLoading])

  // Load personal info when on Step 1 and resource_id exists
  useEffect(() => {
    const loadPersonalInfo = async () => {
      // Only load if we're on Step 1 and have a resource_id
      if (currentStep === 1 && resourceId && !profileLoading) {
        try {
          console.log('Loading personal info for resource_id:', resourceId)
          const response = await vendorService.getPersonalInfo(resourceId)
          
          if (response.success && response.data) {
            const data = response.data
            
            const personalFields = {
              firstName: data.first_name || '',
              lastName: data.last_name || '',
              email: data.email || '',
              phone: data.phone || '',
              alternatePhone: data.alternate_phone || '',
              location: data.location || '',
              address: data.address || '',
              city: data.city || '',
              state: data.state || '',
              country: data.country || '',
              pincode: data.pincode || '',
              dateOfBirth: data.date_of_birth || '',
              gender: data.gender || '',
              linkedinProfile: data.linkedin_profile || '',
              website: data.website_portfolio || ''
            }
            
            // Populate form with fetched data
            setNewResource(prev => {
              const updated = { ...prev, ...personalFields }
              takeStepSnapshot(1, updated)
              return updated
            })
            
            // Mark Step 1 as submitted since data exists
            setStep1Submitted(true)
            
            console.log('Personal info loaded successfully')
          }
        } catch (error) {
          console.error('Error loading personal info:', error)
          // Don't show error toast - just log it, as this is optional pre-fill
        }
      }
    }
    
    loadPersonalInfo()
  }, [currentStep, resourceId, profileLoading])

  // Load professional info when on Step 2 and resource_id exists (only if already submitted)
  useEffect(() => {
    const loadProfessionalInfo = async () => {
      // Only load if we're on Step 2, have a resource_id, AND step2 has been submitted
      if (currentStep === 2 && resourceId && step2Submitted && !profileLoading) {
        try {
          console.log('Loading professional info for resource_id:', resourceId)
          const response = await vendorService.getProfessionalInfo(resourceId)
          
          if (response.success && response.data) {
            const data = response.data
            
            const professionalFields = {
              designation: data.designation || '',
              department: data.department || '',
              totalExperience: data.total_experience || '',
              relevantExperience: data.relevant_experience || '',
              currentCTC: data.current_ctc || '',
              expectedCTC: data.expected_ctc || '',
              noticePeriod: data.notice_period || '',
              availability: data.availability || 'Available',
              costperHour: data.cost_per_hour || '',
              currentProject: data.current_project || '',
              professionalSummary: data.professional_summary || '',
              readyToRelocate: data.ready_to_relocate || false,
              primarySkills: Array.isArray(data.primary_skills) ? data.primary_skills : [],
              secondarySkills: Array.isArray(data.secondary_skills) ? data.secondary_skills : [],
              technologies: Array.isArray(data.technologies) ? data.technologies : [],
              languages: Array.isArray(data.languages) ? data.languages : []
            }
            
            // Populate form with fetched data
            setNewResource(prev => {
              const updated = { ...prev, ...professionalFields }
              takeStepSnapshot(2, updated)
              return updated
            })
            
            console.log('Professional info loaded successfully')
          }
        } catch (error) {
          console.error('Error loading professional info:', error)
          // Don't show error toast - just log it, as this is optional pre-fill
        }
      }
    }
    
    loadProfessionalInfo()
  }, [currentStep, resourceId, step2Submitted, profileLoading])

  // Load education info when on Step 3 and resource_id exists (only if already submitted)
  useEffect(() => {
    const loadEducationInfo = async () => {
      // Only load if we're on Step 3, have a resource_id, AND step3 has been submitted
      if (currentStep === 3 && resourceId && step3Submitted && !profileLoading) {
        try {
          console.log('Loading education info for resource_id:', resourceId)
          const response = await vendorService.getEducationInfo(resourceId)
          
          if (response.success && response.data && Array.isArray(response.data)) {
            // Map API data to form format
            const educationData = response.data.map(edu => ({
              degree: edu.degree || '',
              institution: edu.institution || '',
              fieldOfStudy: edu.field_of_study || '',
              yearOfPassing: edu.year_of_passing || '',
              percentage: edu.percentage || '',
              isHighest: edu.is_highest || false,
              document: null,
              documentUrl: edu.certificate_url || '',
              documentName: edu.certificate_url ? edu.certificate_url.split('/').pop() : ''
            }))
            
            // Populate form with fetched data
            setNewResource(prev => {
              const updated = {
                ...prev,
                education: educationData.length > 0 ? educationData : prev.education
              }
              takeStepSnapshot(3, updated)
              return updated
            })
            
            console.log('Education info loaded successfully')
          }
        } catch (error) {
          console.error('Error loading education info:', error)
          // Don't show error toast - just log it, as this is optional pre-fill
        }
      }
    }
    
    loadEducationInfo()
  }, [currentStep, resourceId, step3Submitted, profileLoading])

  // Load experience info when on Step 4 and resource_id exists (only if already submitted)
  useEffect(() => {
    const loadExperienceInfo = async () => {
      // Only load if we're on Step 4, have a resource_id, AND step4 has been submitted
      if (currentStep === 4 && resourceId && step4Submitted && !profileLoading) {
        try {
          console.log('Loading experience info for resource_id:', resourceId)
          const response = await vendorService.getExperienceInfo(resourceId)
          
          if (response.success && response.data && Array.isArray(response.data)) {
            // Map API data to form format
            const experienceData = response.data.map(exp => ({
              company: exp.company || '',
              designation: exp.designation || '',
              startDate: exp.start_date || '',
              endDate: exp.end_date || '',
              isCurrent: exp.is_current || false,
              description: exp.description || '',
              technologies: Array.isArray(exp.technologies) ? exp.technologies : [],
              offerLetter: null,
              offerLetterUrl: exp.offer_letter_url || '',
              offerLetterName: exp.offer_letter_url ? exp.offer_letter_url.split('/').pop() : '',
              relievingLetter: null,
              relievingLetterUrl: exp.relieving_letter_url || '',
              relievingLetterName: exp.relieving_letter_url ? exp.relieving_letter_url.split('/').pop() : '',
              payslip1: null,
              payslip1Url: exp.payslip_1_url || '',
              payslip1Name: exp.payslip_1_url ? exp.payslip_1_url.split('/').pop() : '',
              payslip2: null,
              payslip2Url: exp.payslip_2_url || '',
              payslip2Name: exp.payslip_2_url ? exp.payslip_2_url.split('/').pop() : '',
              payslip3: null,
              payslip3Url: exp.payslip_3_url || '',
              payslip3Name: exp.payslip_3_url ? exp.payslip_3_url.split('/').pop() : ''
            }))
            
            // Populate form with fetched data
            setNewResource(prev => {
              const updated = {
                ...prev,
                experience: experienceData.length > 0 ? experienceData : prev.experience
              }
              takeStepSnapshot(4, updated)
              return updated
            })
            
            console.log('Experience info loaded successfully')
          }
        } catch (error) {
          console.error('Error loading experience info:', error)
          // Don't show error toast - just log it, as this is optional pre-fill
        }
      }
    }
    
  loadExperienceInfo()
  }, [currentStep, resourceId, step4Submitted, profileLoading])

  // Load certification info when on Step 5 and resource_id exists
  useEffect(() => {
    const loadCertificationInfo = async () => {
      // Load if we're on Step 5, have a resource_id
      if (currentStep === 5 && resourceId && !profileLoading) {
        try {
          console.log('Loading certification info for resource_id:', resourceId)
          const response = await vendorService.getCertificationInfo(resourceId)
          
          if (response.success && response.data && Array.isArray(response.data)) {
            // Map API data to form format
            const certificationData = response.data.map(cert => ({
              name: cert.certification_name || '',
              issuer: cert.issuing_organization || '',
              issueDate: cert.issue_date || '',
              expiryDate: cert.expiry_date || '',
              credentialId: cert.credential_id || '',
              credentialUrl: cert.credential_url || '',
              document: null,
              documentUrl: cert.certificate_url || '',
              documentName: cert.certificate_url ? cert.certificate_url.split('/').pop() : ''
            }))
            
            // Populate form with fetched data
            setNewResource(prev => {
              const updated = {
                ...prev,
                certifications: certificationData.length > 0 ? certificationData : prev.certifications
              }
              takeStepSnapshot(5, updated)
              return updated
            })
            
            console.log('Certification info loaded successfully')
          }
        } catch (error) {
          console.error('Error loading certification info:', error)
          // Don't show error toast - just log it, as this is optional pre-fill
        }
      }
    }
    
    loadCertificationInfo()
  }, [currentStep, resourceId, profileLoading])

  // Load base documents when on Step 6 and resource_id exists
  useEffect(() => {
    const loadBaseDocuments = async () => {
      // Load if we're on Step 6, have a resource_id, and either it's not submitted or we're just entering
      if (currentStep === 6 && resourceId && !profileLoading) {
        try {
          console.log('Loading base documents for resource_id:', resourceId)
          const response = await vendorService.getBaseDocuments(resourceId)
          
          if (response.success && response.data) {
            const data = response.data
            
            // Map API data to form state
            setNewResource(prev => {
              const updated = {
                ...prev,
                documents: {
                  ...prev.documents,
                  resumeUrl: data.resume_url || prev.documents.resumeUrl,
                  coverLetterUrl: data.cover_letter_url || prev.documents.coverLetterUrl,
                  bgvDocumentUrl: data.bgv_pdf_url || prev.documents.bgvDocumentUrl,
                  selfIntroUrl: data.video_resume_url || prev.documents.selfIntroUrl,
                  otherDocuments: (data.other_documents || []).length > 0 
                    ? data.other_documents.map(doc => ({
                        name: doc.name || '',
                        url: doc.url || '',
                        key: doc.key || '',
                        status: doc.status || 'Uploaded'
                      }))
                    : prev.documents.otherDocuments
                }
              }
              takeStepSnapshot(6, updated)
              return updated
            })
            
            console.log('Base documents loaded successfully')
          }
        } catch (error) {
          console.error('Error loading base documents:', error)
          // Don't show error toast for background pre-fill
        }
      }
    }
    
    loadBaseDocuments()
  }, [currentStep, resourceId, profileLoading])

  const handleEditSave = async () => {
    try {
      setAddingResource(true)
      // Placeholder for actual API call as requested
      console.log('Hitting update API for step', currentStep, 'resource', resourceId)
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800))
      
      toast.success('Information updated successfully')
      
      // Take new snapshot to reset dirty state
      takeStepSnapshot(currentStep, newResource)
    } catch (error) {
      console.error('Error in handleEditSave:', error)
      toast.error('Failed to save changes')
    } finally {
      setAddingResource(false)
    }
  }

  const handleNext = async () => {
    // If on Step 1, save personal info first (only if not already submitted)
    if (currentStep === 1) {
      // If already submitted, just move to next step
      if (step1Submitted && resourceId) {
        if (isModal) {
          setInternalStep(currentStep + 1)
        } else {
          setSearchParams({ step: (currentStep + 1).toString() })
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        return
      }

      // Otherwise, submit the data
      try {
        setAddingResource(true)
        
        // Validate required fields for Step 1
        if (!newResource.firstName || !newResource.lastName || !newResource.email || !newResource.phone) {
          toast.error('Please fill in all required fields')
          setAddingResource(false)
          return
        }

        // Get vendor_id and user_id (using same pattern as handleAddResource)
        let vendorId = sessionStorage.getItem('vendorId')
        if (vendorId) {
          try { vendorId = JSON.parse(vendorId) } catch (e) { }
        }
        if (!vendorId && currentUser?.vendor_id) vendorId = currentUser.vendor_id
        
        const userId = currentUser?.id || sessionStorage.getItem('userId')
        
        console.log('Current User:', currentUser)
        console.log('Vendor ID:', vendorId)
        console.log('User ID:', userId)

        if (!vendorId || !userId) {
          console.error('Missing IDs - vendorId:', vendorId, 'userId:', userId)
          toast.error('User information not found. Please login again.')
          setAddingResource(false)
          return
        }

        // Prepare personal info payload
        const personalInfoPayload = {
          vendor_id: parseInt(vendorId),
          user_id: parseInt(userId),
          first_name: newResource.firstName,
          last_name: newResource.lastName,
          email: newResource.email,
          phone: newResource.phone,
          alternate_phone: newResource.alternatePhone || newResource.phone,
          location: newResource.location || '',
          date_of_birth: newResource.dateOfBirth || '',
          gender: newResource.gender || '',
          address: newResource.address || '',
          city: newResource.city || '',
          state: newResource.state || '',
          country: newResource.country || '',
          pincode: newResource.pincode || ''
        }
        
        // Add resource_id if we have one (Edit Mode)
        if (resourceId) {
          personalInfoPayload.resource_id = resourceId
        }

        console.log('=== CALLING PERSONAL INFO API FROM STEP 1 ===')
        console.log('Payload:', personalInfoPayload)
        
        toast.loading('Saving personal information...', { id: 'personal-info' })

        // Call the API
        const response = await vendorService.addPersonalInfo(personalInfoPayload)
        console.log('Personal Info Response:', response)

        if (response.success && response.data) {
          // Store resource_id for subsequent steps
          setResourceId(response.data.resource_id)
          setStep1Submitted(true) // Mark Step 1 as submitted
          console.log('Resource ID saved:', response.data.resource_id)
          toast.success('Personal information saved successfully', { id: 'personal-info' })
          
          // Move to next step
          if (currentStep < totalSteps) {
            if (isModal) {
              setInternalStep(currentStep + 1)
            } else {
              setSearchParams({ step: (currentStep + 1).toString() })
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }
        } else {
          toast.error('Failed to save personal information', { id: 'personal-info' })
        }
        
        setAddingResource(false)
      } catch (error) {
        console.error('Error saving personal info:', error)
        toast.error(error.message || 'Failed to save personal information')
        setAddingResource(false)
      }
    } else if (currentStep === 2) {
      // If already submitted, just move to next step
      if (step2Submitted && resourceId) {
        if (isModal) {
          setInternalStep(currentStep + 1)
        } else {
          setSearchParams({ step: (currentStep + 1).toString() })
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        return
      }

      // Step 2: Save Professional Information
      try {
        setAddingResource(true)
        
        // Validate required fields for Step 2
        if (!newResource.designation) {
          toast.error('Please fill in the designation field')
          setAddingResource(false)
          return
        }

        // Check if we have resource_id from Step 1
        if (!resourceId) {
          toast.error('Please complete Step 1 first')
          setAddingResource(false)
          return
        }

        // Prepare professional info payload
        const professionalInfoPayload = {
          resource_id: resourceId,
          designation: newResource.designation,
          department: newResource.department || '',
          total_experience: newResource.totalExperience || '',
          relevant_experience: newResource.relevantExperience || '',
          current_ctc: newResource.currentCTC || '',
          expected_ctc: newResource.expectedCTC || '',
          notice_period: newResource.noticePeriod || '',
          availability: newResource.availability || 'Available',
          cost_per_hour: newResource.costperHour || '',
          current_project: newResource.currentProject || '',
          professional_summary: newResource.professionalSummary || '',
          ready_to_relocate: newResource.readyToRelocate || false,
          primary_skills: Array.isArray(newResource.primarySkills) ? newResource.primarySkills : [],
          secondary_skills: Array.isArray(newResource.secondarySkills) ? newResource.secondarySkills : [],
          technologies: Array.isArray(newResource.technologies) ? newResource.technologies : [],
          languages: Array.isArray(newResource.languages) ? newResource.languages : []
        }

        console.log('=== CALLING PROFESSIONAL INFO API FROM STEP 2 ===')
        console.log('Payload:', professionalInfoPayload)
        
        toast.loading('Saving professional information...', { id: 'professional-info' })

        // Call the API
        const response = await vendorService.addProfessionalInfo(professionalInfoPayload)
        console.log('Professional Info Response:', response)

        if (response.success) {
          setStep2Submitted(true) // Mark Step 2 as submitted
          toast.success('Professional information saved successfully', { id: 'professional-info' })
          
          // Move to next step
          if (currentStep < totalSteps) {
            if (isModal) {
              setInternalStep(currentStep + 1)
            } else {
              setSearchParams({ step: (currentStep + 1).toString() })
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }
        } else {
          toast.error('Failed to save professional information', { id: 'professional-info' })
        }
        
        setAddingResource(false)
      } catch (error) {
        console.error('Error saving professional info:', error)
        toast.error(error.message || 'Failed to save professional information')
        setAddingResource(false)
      }
    } else if (currentStep === 3) {
      // If already submitted, just move to next step
      if (step3Submitted && resourceId) {
        if (isModal) {
          setInternalStep(currentStep + 1)
        } else {
          setSearchParams({ step: (currentStep + 1).toString() })
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        return
      }

      // Step 3: Save Education Information
      try {
        setAddingResource(true)
        
        // Check if we have resource_id from Step 1
        if (!resourceId) {
          toast.error('Please complete Step 1 first')
          setAddingResource(false)
          return
        }

        // Prepare education data array
        const educationDataArray = newResource.education.map(edu => ({
          degree: edu.degree || '',
          institution: edu.institution || '',
          fieldOfStudy: edu.fieldOfStudy || '',
          yearOfPassing: edu.yearOfPassing || '',
          percentage: edu.percentage || '',
          isHighest: edu.isHighest || false
        }))

        // Collect certificate files and their indexes
        const certificates = []
        const fileIndexes = []
        
        newResource.education.forEach((edu, index) => {
          if (edu.document) {
            certificates.push(edu.document)
            fileIndexes.push(index)
          }
        })

        // Prepare education info payload
        const educationInfoPayload = {
          resource_id: resourceId,
          education_data: educationDataArray,
          certificates: certificates,
          file_indexes: fileIndexes
        }

        console.log('=== CALLING EDUCATION INFO API FROM STEP 3 ===')
        console.log('Payload:', {
          resource_id: educationInfoPayload.resource_id,
          education_data: educationInfoPayload.education_data,
          certificates_count: certificates.length,
          file_indexes: fileIndexes
        })
        
        toast.loading('Saving education information...', { id: 'education-info' })

        // Call the API
        const response = await vendorService.addEducationInfo(educationInfoPayload)
        console.log('Education Info Response:', response)

        if (response.success) {
          setStep3Submitted(true) // Mark Step 3 as submitted
          toast.success('Education information saved successfully', { id: 'education-info' })
          
          // Move to next step
          if (currentStep < totalSteps) {
            if (isModal) {
              setInternalStep(currentStep + 1)
            } else {
              setSearchParams({ step: (currentStep + 1).toString() })
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }
        } else {
          toast.error('Failed to save education information', { id: 'education-info' })
        }
        
        setAddingResource(false)
      } catch (error) {
        console.error('Error saving education info:', error)
        toast.error(error.message || 'Failed to save education information')
        setAddingResource(false)
      }
    } else if (currentStep === 4) {
      // If already submitted, just move to next step
      if (step4Submitted && resourceId) {
        if (isModal) {
          setInternalStep(currentStep + 1)
        } else {
          setSearchParams({ step: (currentStep + 1).toString() })
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        return
      }

      // Step 4: Save Experience Information
      try {
        setAddingResource(true)
        
        // Check if we have resource_id from Step 1
        if (!resourceId) {
          toast.error('Please complete Step 1 first')
          setAddingResource(false)
          return
        }

        // Prepare experience data array
        const experienceDataArray = newResource.experience.map(exp => ({
          company: exp.company || '',
          designation: exp.designation || '',
          startDate: exp.startDate || '',
          endDate: exp.endDate || '',
          isCurrent: exp.isCurrent || false,
          description: exp.description || '',
          technologies: Array.isArray(exp.technologies) ? exp.technologies : []
        }))

        // Collect all document files and their mappings
        const certificates = []
        const fileIndexes = []
        
        newResource.experience.forEach((exp, expIdx) => {
          // Offer letter
          if (exp.offerLetter) {
            fileIndexes.push({
              exp_idx: expIdx,
              slot: 'offer_letter'
            })
            certificates.push(exp.offerLetter)
          }
          
          // Relieving letter
          if (exp.relievingLetter) {
            fileIndexes.push({
              exp_idx: expIdx,
              slot: 'relieving_letter'
            })
            certificates.push(exp.relievingLetter)
          }
          
          // Payslip 1
          if (exp.payslip1) {
            fileIndexes.push({
              exp_idx: expIdx,
              slot: 'payslip_1'
            })
            certificates.push(exp.payslip1)
          }
          
          // Payslip 2
          if (exp.payslip2) {
            fileIndexes.push({
              exp_idx: expIdx,
              slot: 'payslip_2'
            })
            certificates.push(exp.payslip2)
          }
          
          // Payslip 3
          if (exp.payslip3) {
            fileIndexes.push({
              exp_idx: expIdx,
              slot: 'payslip_3'
            })
            certificates.push(exp.payslip3)
          }
        })

        // Prepare experience info payload
        const experienceInfoPayload = {
          resource_id: resourceId,
          experience_data: experienceDataArray,
          certificates: certificates,
          file_indexes: fileIndexes
        }

        console.log('=== CALLING EXPERIENCE INFO API FROM STEP 4 ===')
        console.log('Payload:', {
          resource_id: experienceInfoPayload.resource_id,
          experience_data: experienceInfoPayload.experience_data,
          certificates_count: certificates.length,
          file_indexes: fileIndexes
        })
        
        toast.loading('Saving experience information...', { id: 'experience-info' })

        // Call the API
        const response = await vendorService.addExperienceInfo(experienceInfoPayload)
        console.log('Experience Info Response:', response)

        if (response.success) {
          setStep4Submitted(true) // Mark Step 4 as submitted
          toast.success('Experience information saved successfully', { id: 'experience-info' })
          
          // Move to next step
          if (currentStep < totalSteps) {
            if (isModal) {
              setInternalStep(currentStep + 1)
            } else {
              setSearchParams({ step: (currentStep + 1).toString() })
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }
        } else {
          toast.error('Failed to save experience information', { id: 'experience-info' })
        }
        
        setAddingResource(false)
      } catch (error) {
        console.error('Error saving experience info:', error)
        toast.error(error.message || 'Failed to save experience information')
        setAddingResource(false)
      }
    } else if (currentStep === 5) {
      // If already submitted, just move to next step
      if (step5Submitted && resourceId) {
        if (isModal) {
          setInternalStep(currentStep + 1)
        } else {
          setSearchParams({ step: (currentStep + 1).toString() })
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
        return
      }

      // Step 5: Save Certification Information
      try {
        setAddingResource(true)
        
        // Check if we have resource_id from Step 1
        if (!resourceId) {
          toast.error('Please complete Step 1 first')
          setAddingResource(false)
          return
        }

        // Prepare certification data array
        const certificationDataArray = (newResource.certifications || []).map(cert => ({
          certificationName: cert.name || '',
          issuingOrganization: cert.issuer || '',
          issueDate: cert.issueDate || '',
          expiryDate: cert.expiryDate || '',
          credentialId: cert.credentialId || '',
          credentialUrl: cert.credentialUrl || ''
        }))

        // Collect certificate files and their indexes
        const certificates = []
        const fileIndexes = []
        
        ;(newResource.certifications || []).forEach((cert, index) => {
          if (cert.document) {
            certificates.push(cert.document)
            fileIndexes.push(index)
          }
        })

        // Prepare certification info payload
        const certificationInfoPayload = {
          resource_id: resourceId,
          certification_data: certificationDataArray,
          certificates: certificates,
          file_indexes: fileIndexes
        }

        console.log('=== CALLING CERTIFICATION INFO API FROM STEP 5 ===')
        console.log('Payload:', {
          resource_id: certificationInfoPayload.resource_id,
          certification_data_count: certificationDataArray.length,
          certificates_count: certificates.length,
          file_indexes: fileIndexes
        })
        
        toast.loading('Saving certification information...', { id: 'certification-info' })

        // Call the API
        const response = await vendorService.addCertificationInfo(certificationInfoPayload)
        console.log('Certification Info Response:', response)

        if (response.success) {
          setStep5Submitted(true) // Mark Step 5 as submitted
          toast.success('Certification information saved successfully', { id: 'certification-info' })
          
          // Move to next step
          if (currentStep < totalSteps) {
            if (isModal) {
              setInternalStep(currentStep + 1)
            } else {
              setSearchParams({ step: (currentStep + 1).toString() })
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }
        } else {
          toast.error('Failed to save certification information', { id: 'certification-info' })
        }
        
        setAddingResource(false)
      } catch (error) {
        console.error('Error saving certification info:', error)
        toast.error(error.message || 'Failed to save certification information')
        setAddingResource(false)
      }
    } else {
      // For other steps, just move forward
      if (currentStep < totalSteps) {
        if (isModal) {
          setInternalStep(currentStep + 1)
        } else {
          setSearchParams({ step: (currentStep + 1).toString() })
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      }
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      if (isModal) {
        setInternalStep(currentStep - 1)
      } else {
        setSearchParams({ step: (currentStep - 1).toString() })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  }

  // Helper function to upload a file to S3
  const uploadFileToS3 = async (file, documentType, documentName, vendorId) => {
    if (!file) return null
    
    try {
      const uploadResponse = await vendorService.uploadDocument({
        file: file,
        document_type: documentType,
        document_name: documentName || file.name,
        document_category: 'resource',
        user_role: 'vendor',
        vendor_id: vendorId
      })
      
      // Extract URL from response - adjust based on your API response structure
      const url = uploadResponse?.data?.url || 
                  uploadResponse?.data?.file_url || 
                  uploadResponse?.data?.document_url ||
                  uploadResponse?.url ||
                  uploadResponse?.file_url ||
                  uploadResponse?.document_url
      
      return url || null
    } catch (error) {
      console.error(`Error uploading ${documentType}:`, error)
      throw new Error(`Failed to upload ${documentName || documentType}`)
    }
  }

  const handleAddResource = async () => {
    try {
      if (!isVendorActive) {
        toast.error('Vendor account is not active. Cannot add resources.')
        return
      }
      setAddingResource(true)
      
      // Validate required fields
      if (!newResource.firstName || !newResource.lastName || !newResource.email || !newResource.designation) {
        toast.error('Please fill in all required fields')
        setAddingResource(false)
        return
      }

      // Get vendor ID
      let vendorId = sessionStorage.getItem('vendorId')
      if (vendorId) {
        try { vendorId = JSON.parse(vendorId) } catch (e) { }
      }
      if (!vendorId && currentUser?.vendor_id) vendorId = currentUser.vendor_id
      if (!vendorId) {
        toast.error('Vendor ID not found. Please login again.')
        setAddingResource(false)
        return
      }

      // Step 1: Save Personal Information first
      toast.loading('Saving personal information...', { id: 'personal-info' })
      
      const userId = currentUser?.id || sessionStorage.getItem('userId')
      const personalInfoPayload = {
        vendor_id: parseInt(vendorId),
        user_id: parseInt(userId),
        name: `${newResource.firstName} ${newResource.lastName}`.trim(),
        email: newResource.email,
        phone: newResource.phone,
        alternate_phone: newResource.alternatePhone || newResource.phone,
        location: newResource.location || '',
        date_of_birth: newResource.dateOfBirth || '',
        gender: newResource.gender || '',
        address: newResource.address || '',
        city: newResource.city || '',
        state: newResource.state || '',
        country: newResource.country || '',
        pincode: newResource.pincode || ''
      }

      console.log('=== PERSONAL INFO API CALL ===')
      console.log('Payload:', personalInfoPayload)
      
      try {
        const personalInfoResponse = await vendorService.addPersonalInfo(personalInfoPayload)
        console.log('Personal Info Response:', personalInfoResponse)
        
        if (!personalInfoResponse.success) {
          toast.error('Failed to save personal information', { id: 'personal-info' })
          setAddingResource(false)
          return
        }
        
        // Store resource_id from response
        const savedResourceId = personalInfoResponse.data.resource_id
        setResourceId(savedResourceId)
        console.log('Resource ID saved:', savedResourceId)
        toast.success('Personal information saved', { id: 'personal-info' })
      } catch (error) {
        console.error('Error calling personal info API:', error)
        toast.error(error.message || 'Failed to save personal information', { id: 'personal-info' })
        setAddingResource(false)
        return
      }

      toast.loading('Uploading documents...', { id: 'upload-progress' })

      // Upload education documents
      const educationWithUrls = await Promise.all(
        newResource.education.map(async (edu) => {
          let documentUrl = edu.documentUrl || ''
          if (edu.document && !documentUrl) {
            documentUrl = await uploadFileToS3(
              edu.document,
              'education_document',
              edu.documentName || `${edu.degree}_${edu.institution}`,
              vendorId
            ) || ''
          }
          return {
            degree: edu.degree,
            institution: edu.institution,
            fieldOfStudy: edu.fieldOfStudy,
            yearOfPassing: edu.yearOfPassing,
            percentage: edu.percentage,
            isHighest: edu.isHighest,
            documentUrl: documentUrl,
            documentName: edu.documentName || ''
          }
        })
      )

      // Upload experience documents
      const experienceWithUrls = await Promise.all(
        newResource.experience.map(async (exp) => {
          let documentUrl = exp.documentUrl || ''
          if (exp.document && !documentUrl) {
            documentUrl = await uploadFileToS3(
              exp.document,
              'experience_document',
              exp.documentName || `${exp.company}_${exp.designation}`,
              vendorId
            ) || ''
          }
          return {
            company: exp.company,
            designation: exp.designation,
            startDate: exp.startDate,
            endDate: exp.endDate,
            isCurrent: exp.isCurrent,
            description: exp.description,
            technologies: Array.isArray(exp.technologies) ? exp.technologies : [],
            documentUrl: documentUrl,
            documentName: exp.documentName || ''
          }
        })
      )

      // Upload certification documents
      const certificationsWithUrls = await Promise.all(
        (newResource.certifications || []).map(async (cert) => {
          let documentUrl = cert.documentUrl || ''
          if (cert.document && !documentUrl) {
            documentUrl = await uploadFileToS3(
              cert.document,
              'certification_document',
              cert.documentName || cert.name,
              vendorId
            ) || ''
          }
          return {
            certificationName: cert.name || '',
            issuingOrganization: cert.issuer || '',
            issueDate: cert.issueDate || '',
            expiryDate: cert.expiryDate || '',
            credentialId: cert.credentialId || '',
            credentialUrl: cert.credentialUrl || '',
            documentUrl: documentUrl,
            documentName: cert.documentName || ''
          }
        })
      )

      // Upload resume
      let resumeUrl = newResource.documents.resumeUrl || ''
      if (newResource.documents.resume && !resumeUrl) {
        resumeUrl = await uploadFileToS3(
          newResource.documents.resume,
          'resume',
          'Resume',
          vendorId
        ) || ''
      }

      // Upload cover letter
      let coverLetterUrl = newResource.documents.coverLetterUrl || ''
      if (newResource.documents.coverLetter && !coverLetterUrl) {
        coverLetterUrl = await uploadFileToS3(
          newResource.documents.coverLetter,
          'cover_letter',
          'Cover Letter',
          vendorId
        ) || ''
      }

      // Upload BGV document
      let bgvDocumentUrl = newResource.documents.bgvDocumentUrl || ''
      if (newResource.documents.bgvDocument && !bgvDocumentUrl) {
        bgvDocumentUrl = await uploadFileToS3(
          newResource.documents.bgvDocument,
          'bgv_document',
          newResource.documents.bgvDocumentName || 'BGV Document',
          vendorId
        ) || ''
      }

      // Upload self intro
      let selfIntroUrl = newResource.documents.selfIntroUrl || ''
      if (newResource.documents.selfIntro && !selfIntroUrl) {
        selfIntroUrl = await uploadFileToS3(
          newResource.documents.selfIntro,
          'self_intro',
          newResource.documents.selfIntroName || 'Self Intro',
          vendorId
        ) || ''
      }

      // Upload other documents
      const otherDocumentsWithUrls = await Promise.all(
        (newResource.documents.otherDocuments || []).map(async (doc) => {
          if (doc.file && !doc.url) {
            const url = await uploadFileToS3(
              doc.file,
              'other_document',
              doc.name || 'Other Document',
              vendorId
            ) || ''
            return { ...doc, url }
          }
          return doc
        })
      )

      toast.dismiss('upload-progress')
      toast.loading('Saving resource data...', { id: 'save-progress' })

      // Prepare resource data with URLs (not file objects) - matching API format
      const resourceData = {
        // Personal Information
        name: `${newResource.firstName} ${newResource.lastName}`.trim(),
        email: newResource.email,
        phone: newResource.phone || '',
        location: newResource.location || '',
        dateOfBirth: newResource.dateOfBirth || '',
        gender: newResource.gender || '',
        
        // Professional Details
        designation: newResource.designation,
        department: newResource.department || '',
        totalExperience: newResource.totalExperience || '',
        relevantExperience: newResource.relevantExperience || '',
        currentCTC: newResource.currentCTC || '',
        expectedCTC: newResource.expectedCTC || '',
        costperHour: newResource.costperHour ? parseFloat(newResource.costperHour) : null,
        noticePeriod: newResource.noticePeriod || '',
        availability: newResource.availability || 'Available',
        readyToRelocate: newResource.readyToRelocate || false,
        
        // Skills (arrays)
        primarySkills: Array.isArray(newResource.primarySkills) ? newResource.primarySkills : [],
        secondarySkills: Array.isArray(newResource.secondarySkills) ? newResource.secondarySkills : [],
        technologies: Array.isArray(newResource.technologies) ? newResource.technologies : [],
        
        // Education (array of objects - remove documentUrl/documentName if not needed by API)
        education: educationWithUrls.map(edu => ({
          degree: edu.degree || '',
          institution: edu.institution || '',
          fieldOfStudy: edu.fieldOfStudy || '',
          yearOfPassing: edu.yearOfPassing || '',
          percentage: edu.percentage || '',
          isHighest: edu.isHighest || false
        })),
        
        // Professional Experience (array of objects - remove documentUrl/documentName if not needed by API)
        experience: experienceWithUrls.map(exp => ({
          company: exp.company || '',
          designation: exp.designation || '',
          startDate: exp.startDate || '',
          endDate: exp.endDate || '',
          isCurrent: exp.isCurrent || false,
          description: exp.description || '',
          technologies: Array.isArray(exp.technologies) ? exp.technologies : []
        })),
        
        // Certifications (array of objects - using API field names)
        certifications: certificationsWithUrls.map(cert => ({
          certificationName: cert.certificationName || '',
          issuingOrganization: cert.issuingOrganization || '',
          issueDate: cert.issueDate || '',
          expiryDate: cert.expiryDate || '',
          credentialId: cert.credentialId || ''
        })),
        
        // Languages (array of objects with languageName and proficiencyLevel)
        languages: Array.isArray(newResource.languages) ? newResource.languages.map(lang => {
          // Handle both object format and string format
          if (typeof lang === 'string') {
            return {
              languageName: lang,
              proficiencyLevel: 'Intermediate'
            }
          }
          return {
            languageName: lang.languageName || lang.name || lang.language || '',
            proficiencyLevel: lang.proficiencyLevel || lang.proficiency || lang.level || 'Intermediate'
          }
        }) : [],
        
        // Documents
        resumeUrl: resumeUrl || '',
        coverLetterUrl: coverLetterUrl || '',
        bgvDocumentUrl: bgvDocumentUrl || '',
        selfIntroUrl: selfIntroUrl || '',
        otherDocuments: otherDocumentsWithUrls.map(doc => ({
          name: doc.name || '',
          url: doc.url || ''
        }))
      }

      const response = await vendorService.addVendorResource(resourceData)
      setStep6Submitted(true)
      
      toast.dismiss('save-progress')
      
      toast.success('Resource added successfully!')
      
      // Clear saved form data
      localStorage.removeItem('newResourceForm')
      
      // Handle modal vs page mode
      if (isModal) {
        resetForm()
        if (onSuccess) onSuccess()
        if (onClose) onClose()
      } else {
        // Navigate back to bench resources
        navigate('/vendor/dashboard/bench-resources', { replace: true })
      }
    } catch (error) {
      console.error('Error adding resource:', error)
      
      // Show detailed error message
      let errorMessage = 'Failed to add resource'
      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data) {
        const errorData = error.response.data
        if (errorData.detail) {
          if (Array.isArray(errorData.detail)) {
            errorMessage = errorData.detail.map(err => 
              `${err.loc?.join('.')}: ${err.msg}`
            ).join('\n')
          } else {
            errorMessage = JSON.stringify(errorData.detail)
          }
        } else if (errorData.message) {
          errorMessage = errorData.message
        }
      }
      
      toast.error(errorMessage, { duration: 5000 })
    } finally {
      setAddingResource(false)
    }
  }

  const resetForm = () => {
    localStorage.removeItem('newResourceForm')
    setNewResource({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      location: '',
      dateOfBirth: '',
      gender: '',
      designation: '',
      department: '',
      totalExperience: '',
      relevantExperience: '',
      currentCTC: '',
      expectedCTC: '',
      noticePeriod: '',
      availability: 'Available',
      currentProject: '',
      joiningDate: '',
      costperHour: '',
      readyToRelocate: false,
      education: [{ degree: '', institution: '', fieldOfStudy: '', yearOfPassing: '', percentage: '', isHighest: false }],
      experience: [{ 
        company: '', 
        designation: '', 
        startDate: '', 
        endDate: '', 
        isCurrent: false, 
        description: '', 
        technologies: [],
        offerLetter: null,
        offerLetterUrl: '',
        offerLetterName: '',
        relievingLetter: null,
        relievingLetterUrl: '',
        relievingLetterName: '',
        payslip1: null,
        payslip1Url: '',
        payslip1Name: '',
        payslip2: null,
        payslip2Url: '',
        payslip2Name: '',
        payslip3: null,
        payslip3Url: '',
        payslip3Name: ''
      }],
      certifications: [],
      primarySkills: [],
      secondarySkills: [],
      technologies: [],
      languages: [],
      documents: { 
        resume: null, 
        resumeUrl: '', 
        coverLetter: null, 
        coverLetterUrl: '', 
        bgvDocument: null,
        bgvDocumentUrl: '',
        bgvDocumentName: '',
        selfIntro: null,
        selfIntroUrl: '',
        selfIntroName: '',
        otherDocuments: [] 
      }
    })
    setResourceId(null)
    setStep1Submitted(false)
    setStep2Submitted(false)
    setStep3Submitted(false)
    setStep4Submitted(false)
    setStep5Submitted(false)
    setStep6Submitted(false)
    if (isModal) {
      setInternalStep(1)
    } else {
      setSearchParams({ step: '1' })
    }
  }

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    )
  }

  if (!isVendorActive) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card p-8 max-w-xl text-center">
          <h2 className="text-2xl font-semibold mb-2">Vendor account not active</h2>
          <p className="text-secondary-600 mb-4">Your vendor account needs to be active to add resources.</p>
          <button
            onClick={() => navigate('/vendor/dashboard/bench-resources')}
            className="btn btn-primary btn-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className={isModal ? "space-y-4" : "space-y-6"}>
      {/* Header - Only show in page mode */}
      {!isModal && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {editId ? 'Edit Bench Resource' : 'Add Bench Resource'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {editId ? 'Update the details of your bench resource' : 'Fill in the details to add a new resource to your bench'}
            </p>
          </div>
        </div>
      )}

      {/* Enhanced Step Indicator */}
      <div className={`bg-gradient-to-r from-primary-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 rounded-lg ${isModal ? 'p-2' : 'p-3'} border border-primary-200 dark:border-gray-700 shadow-md`}>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">
            Step {currentStep} of {totalSteps}
          </h2>
          <div className="text-xs text-gray-600 dark:text-gray-400">
            {Math.round((currentStep / totalSteps) * 100)}% Complete
          </div>
        </div>
        <div className="flex items-center justify-between">
          {[1, 2, 3, 4, 5, 6].map((step) => (
            <div key={step} className="flex items-center flex-1">
              <div className="flex flex-col items-center relative">
                <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs transition-all duration-300 shadow-sm ${
                  currentStep === step 
                    ? 'bg-primary-600 text-white scale-105 ring-2 ring-primary-200 dark:ring-primary-800' 
                    : currentStep > step 
                    ? 'bg-green-500 text-white scale-100' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 scale-100'
                }`}>
                  {currentStep > step ? <CheckCircle className="w-4 h-4" /> : step}
                </div>
                <span className={`text-[10px] mt-1.5 font-medium text-center max-w-[80px] ${
                  currentStep === step 
                    ? 'text-primary-700 dark:text-primary-400' 
                    : currentStep > step 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {step === 1 ? 'Personal Info' : step === 2 ? 'Professional' : step === 3 ? 'Education' : step === 4 ? 'Experience' : step === 5 ? 'Certifications' : 'Documents'}
                </span>
              </div>
              {step < totalSteps && (
                <div className={`flex-1 h-0.5 mx-1.5 rounded-full transition-all duration-300 ${
                  currentStep > step 
                    ? 'bg-green-500 shadow-sm' 
                    : currentStep === step
                    ? 'bg-primary-300 dark:bg-primary-700'
                    : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Form Content */}
  <form onSubmit={(e) => { 
    e.preventDefault(); 
    if (isStepDirty()) {
      handleEditSave();
    } else if (currentStep === totalSteps) {
      handleAddResource();
    } else {
      handleNext();
    }
  }} className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 ${isModal ? 'p-3' : 'p-4'} shadow-md`}>
        {/* Step 1: Personal Information */}
        {currentStep === 1 && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900/50 dark:to-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                <User className="w-4 h-4 mr-2" />
                Personal Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={newResource.firstName}
                      onChange={(e) => setNewResource({...newResource, firstName: e.target.value})}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="Enter first name"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={newResource.lastName}
                      onChange={(e) => setNewResource({...newResource, lastName: e.target.value})}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="Enter last name"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email"
                      value={newResource.email}
                      onChange={(e) => setNewResource({...newResource, email: e.target.value})}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={newResource.phone}
                      onChange={(e) => setNewResource({...newResource, phone: e.target.value})}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Alternate Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel"
                      value={newResource.alternatePhone}
                      onChange={(e) => setNewResource({...newResource, alternatePhone: e.target.value})}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="Alternate phone number"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Location
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={newResource.location}
                      onChange={(e) => setNewResource({...newResource, location: e.target.value})}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="e.g., New York, USA"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Date of Birth
                  </label>
                  <DatePicker
                    value={newResource.dateOfBirth}
                    onChange={(date) => setNewResource({...newResource, dateOfBirth: date})}
                    placeholder="Select date of birth"
                    maxDate={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Gender
                  </label>
                  <select
                    value={newResource.gender}
                    onChange={(e) => setNewResource({...newResource, gender: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    LinkedIn Profile
                  </label>
                  <div className="relative">
                    <Linkedin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={newResource.linkedinProfile}
                      onChange={(e) => setNewResource({...newResource, linkedinProfile: e.target.value})}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="LinkedIn profile URL"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Website/Portfolio
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={newResource.website}
                      onChange={(e) => setNewResource({...newResource, website: e.target.value})}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                      placeholder="Website or portfolio URL"
                    />
                  </div>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Address
                  </label>
                  <textarea
                    value={newResource.address}
                    onChange={(e) => setNewResource({...newResource, address: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    City
                  </label>
                  <input
                    type="text"
                    value={newResource.city}
                    onChange={(e) => setNewResource({...newResource, city: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    State
                  </label>
                  <input
                    type="text"
                    value={newResource.state}
                    onChange={(e) => setNewResource({...newResource, state: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="State"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Country
                  </label>
                  <input
                    type="text"
                    value={newResource.country}
                    onChange={(e) => setNewResource({...newResource, country: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="Country"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Pincode/ZIP
                  </label>
                  <input
                    type="text"
                    value={newResource.pincode}
                    onChange={(e) => setNewResource({...newResource, pincode: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="Pincode/ZIP code"
                  />
                </div>
              </div>
            
            </div>
          </motion.div>
        )}

  {/* Step 6: BGV & Self Intro */}
  {currentStep === 6 && (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900/50 dark:to-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
        <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
          <FileText className="w-4 h-4 mr-2" />
          Documents
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pb-6 mb-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Resume/CV
            </label>
            <div className="flex items-center space-x-4">
              {newResource.documents.resumeUrl ? (
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <FileText className="w-5 h-5" />
                  <span>Resume uploaded</span>
                  <button
                    type="button"
                    onClick={() => {
                      setNewResource({
                        ...newResource,
                        documents: {...newResource.documents, resume: null, resumeUrl: ''}
                      })
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No resume uploaded</div>
              )}
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    const reader = new FileReader()
                    reader.onloadend = () => {
                      setNewResource({
                        ...newResource,
                        documents: {...newResource.documents, resume: file, resumeUrl: reader.result}
                      })
                    }
                    reader.readAsDataURL(file)
                  }
                }}
                className="hidden"
                id="resume-upload-step6"
              />
              <label
                htmlFor="resume-upload-step6"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer inline-flex items-center text-sm transition-all"
              >
                <Upload className="w-4 h-4 mr-2" />
                {newResource.documents.resumeUrl ? 'Change Resume' : 'Upload Resume'}
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Cover Letter
            </label>
            <div className="flex items-center space-x-4">
              {newResource.documents.coverLetterUrl ? (
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <FileText className="w-5 h-5" />
                  <span>Cover letter uploaded</span>
                  <button
                    type="button"
                    onClick={() => {
                      setNewResource({
                        ...newResource,
                        documents: {...newResource.documents, coverLetter: null, coverLetterUrl: ''}
                      })
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No cover letter uploaded</div>
              )}
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    const reader = new FileReader()
                    reader.onloadend = () => {
                      setNewResource({
                        ...newResource,
                        documents: {...newResource.documents, coverLetter: file, coverLetterUrl: reader.result}
                      })
                    }
                    reader.readAsDataURL(file)
                  }
                }}
                className="hidden"
                id="cover-letter-upload-step6"
              />
              <label
                htmlFor="cover-letter-upload-step6"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer inline-flex items-center text-sm transition-all"
              >
                <Upload className="w-4 h-4 mr-2" />
                {newResource.documents.coverLetterUrl ? 'Change Cover Letter' : 'Upload Cover Letter'}
              </label>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
              Background Verification Document (BGV)
            </label>
            <div className="flex items-center space-x-4">
              {newResource.documents.bgvDocumentUrl ? (
                <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                  <FileText className="w-5 h-5" />
                  <span>{newResource.documents.bgvDocumentName || 'BGV document uploaded'}</span>
                  <button
                    type="button"
                    onClick={() => setNewResource({ ...newResource, documents: { ...newResource.documents, bgvDocument: null, bgvDocumentUrl: '', bgvDocumentName: '' } })}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No BGV document uploaded</div>
              )}
              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) {
                    const reader = new FileReader()
                    reader.onloadend = () => {
                      setNewResource({ ...newResource, documents: { ...newResource.documents, bgvDocument: file, bgvDocumentUrl: reader.result, bgvDocumentName: file.name } })
                    }
                    reader.readAsDataURL(file)
                  }
                }}
                className="hidden"
                id="bgv-upload-step"
              />
              <label
                htmlFor="bgv-upload-step"
                className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer inline-flex items-center text-sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                {newResource.documents.bgvDocumentUrl ? 'Change BGV Document' : 'Upload BGV Document'}
              </label>
            </div>
              </div>



              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Self Intro (Upload)
                </label>
                <div className="flex items-center space-x-4">
                  {newResource.documents.selfIntroUrl ? (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <FileText className="w-5 h-5" />
                      <span>{newResource.documents.selfIntroName || 'Self intro uploaded'}</span>
                      <button
                        type="button"
                        onClick={() => setNewResource({ ...newResource, documents: { ...newResource.documents, selfIntro: null, selfIntroUrl: '', selfIntroName: '' } })}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">No self intro uploaded</div>
                  )}
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      const file = e.target.files[0]
                      if (file) {
                        const url = URL.createObjectURL(file)
                        setNewResource({ ...newResource, documents: { ...newResource.documents, selfIntro: file, selfIntroUrl: url, selfIntroName: file.name } })
                      }
                    }}
                    className="hidden"
                    id="selfintro-upload-step"
                  />
                  <label
                    htmlFor="selfintro-upload-step"
                className="px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer inline-flex items-center text-sm"
              >
                <Upload className="w-4 h-4 mr-2" />
                {newResource.documents.selfIntroUrl ? 'Change Self Intro' : 'Upload Self Intro'}
              </label>
            </div>

            {newResource.documents.selfIntroUrl && (
              <div className="mt-2">
                <video src={newResource.documents.selfIntroUrl} controls className="w-full max-w-md rounded-md border" />
                <div className="text-sm text-gray-600 mt-1">{newResource.documents.selfIntroName}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )}

        {/* Step 2: Professional Details */}
        {currentStep === 2 && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900/50 dark:to-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700 flex items-center">
                <Briefcase className="w-4 h-4 mr-2" />
                Professional Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Designation <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newResource.designation}
                    onChange={(e) => setNewResource({...newResource, designation: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                    placeholder="e.g., Senior Developer"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Department
                  </label>
                  <input
                    type="text"
                    value={newResource.department}
                    onChange={(e) => setNewResource({...newResource, department: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                    placeholder="e.g., Engineering"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Total Experience (Years)
                  </label>
                  <input
                    type="text"
                    value={newResource.totalExperience}
                    onChange={(e) => setNewResource({...newResource, totalExperience: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="e.g., 5.5 or 5 years 6 months"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Relevant Experience (Years)
                  </label>
                  <input
                    type="text"
                    value={newResource.relevantExperience}
                    onChange={(e) => setNewResource({...newResource, relevantExperience: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="e.g., 4.0 or 4 years"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Current CTC
                  </label>
                  <input
                    type="text"
                    value={newResource.currentCTC}
                    onChange={(e) => setNewResource({...newResource, currentCTC: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                    placeholder="e.g., ₹10 LPA"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Expected CTC
                  </label>
                  <input
                    type="text"
                    value={newResource.expectedCTC}
                    onChange={(e) => setNewResource({...newResource, expectedCTC: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                    placeholder="e.g., ₹12 LPA"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Notice Period
                  </label>
                  <input
                    type="text"
                    value={newResource.noticePeriod}
                    onChange={(e) => setNewResource({...newResource, noticePeriod: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                    placeholder="e.g., 30 days"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Availability Status
                  </label>
                  <select
                    value={newResource.availability}
                    onChange={(e) => setNewResource({...newResource, availability: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                  >
                    <option value="Available">Available</option>
                    <option value="On Project">On Project</option>
                    <option value="Unavailable">Unavailable</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Cost per Hour ($)
                  </label>
                  <input
                    type="text"
                    value={newResource.costperHour}
                    onChange={(e) => setNewResource({...newResource, costperHour: e.target.value})}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="e.g., 50 or 50.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Current Project
                  </label>
                  <input
                    type="text"
                    value={newResource.currentProject}
                    onChange={(e) => setNewResource({...newResource, currentProject: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                    placeholder="Current project name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Professional Summary
                  </label>
                  <textarea
                    value={newResource.summary}
                    onChange={(e) => setNewResource({...newResource, summary: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                    placeholder="Brief summary of professional background and expertise..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                    Ready to Relocate <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center cursor-pointer group">
                      <div className={`relative w-6 h-6 rounded-full border-2 transition-all ${
                        newResource.readyToRelocate === true
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                      }`}>
                        <input
                          type="radio"
                          name="readyToRelocate"
                          value="yes"
                          checked={newResource.readyToRelocate === true}
                          onChange={() => setNewResource({...newResource, readyToRelocate: true})}
                          className="absolute opacity-0 cursor-pointer w-full h-full"
                        />
                        {newResource.readyToRelocate === true && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-white"></div>
                          </div>
                        )}
                      </div>
                      <span className={`ml-3 font-medium transition-colors ${
                        newResource.readyToRelocate === true
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                      }`}>
                        Yes
                      </span>
                    </label>
                    <label className="flex items-center cursor-pointer group">
                      <div className={`relative w-6 h-6 rounded-full border-2 transition-all ${
                        newResource.readyToRelocate === false
                          ? 'border-primary-600 bg-primary-600'
                          : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
                      }`}>
                        <input
                          type="radio"
                          name="readyToRelocate"
                          value="no"
                          checked={newResource.readyToRelocate === false}
                          onChange={() => setNewResource({...newResource, readyToRelocate: false})}
                          className="absolute opacity-0 cursor-pointer w-full h-full"
                        />
                        {newResource.readyToRelocate === false && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-3 h-3 rounded-full bg-white"></div>
                          </div>
                        )}
                      </div>
                      <span className={`ml-3 font-medium transition-colors ${
                        newResource.readyToRelocate === false
                          ? 'text-primary-600 dark:text-primary-400'
                          : 'text-gray-700 dark:text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400'
                      }`}>
                        No
                      </span>
                    </label>
                  </div>
                </div>
                
                {/* Skills & Technologies */}
                <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Primary Skills (comma separated)
                      </label>
                      <input
                        type="text"
                        value={primarySkillsInput}
                        onChange={(e) => setPrimarySkillsInput(e.target.value)}
                        onBlur={() => {
                          const skills = primarySkillsInput.split(',').map(s => s.trim()).filter(s => s)
                          setNewResource({...newResource, primarySkills: skills})
                        }}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                        placeholder="e.g., React, Node.js, Python"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Secondary Skills (comma separated)
                      </label>
                      <input
                        type="text"
                        value={secondarySkillsInput}
                        onChange={(e) => setSecondarySkillsInput(e.target.value)}
                        onBlur={() => {
                          const skills = secondarySkillsInput.split(',').map(s => s.trim()).filter(s => s)
                          setNewResource({...newResource, secondarySkills: skills})
                        }}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                        placeholder="e.g., MongoDB, AWS, Docker"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Technologies (comma separated)
                      </label>
                      <input
                        type="text"
                        value={technologiesInput}
                        onChange={(e) => setTechnologiesInput(e.target.value)}
                        onBlur={() => {
                          const techs = technologiesInput.split(',').map(t => t.trim()).filter(t => t)
                          setNewResource({...newResource, technologies: techs})
                        }}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                        placeholder="e.g., JavaScript, TypeScript, Git"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                        Languages (comma separated)
                      </label>
                      <input
                        type="text"
                        value={languagesInput}
                        onChange={(e) => setLanguagesInput(e.target.value)}
                        onBlur={() => {
                          const langs = languagesInput.split(',').map(l => l.trim()).filter(l => l)
                          setNewResource({...newResource, languages: langs})
                        }}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white transition-all"
                        placeholder="e.g., English, Hindi, Spanish"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Educational Background */}
        {currentStep === 3 && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Education */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6 pb-3 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Educational Background
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    setNewResource({
                      ...newResource,
                      education: [...newResource.education, { degree: '', institution: '', fieldOfStudy: '', yearOfPassing: '', percentage: '', isHighest: false, document: null, documentUrl: '', documentName: '' }]
                    })
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center text-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Education
                </button>
              </div>
              <div className="space-y-6">
                {newResource.education.map((edu, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="font-semibold text-gray-900 dark:text-white">Education {index + 1}</h5>
                      {newResource.education.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newEdu = newResource.education.filter((_, i) => i !== index)
                            setNewResource({...newResource, education: newEdu})
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Degree <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={edu.degree}
                          onChange={(e) => {
                            const newEdu = [...newResource.education]
                            newEdu[index].degree = e.target.value
                            setNewResource({...newResource, education: newEdu})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                          required
                        >
                          <option value="">Select Degree</option>
                          <option value="High School">High School</option>
                          <option value="Diploma">Diploma</option>
                          <option value="B.Tech">B.Tech</option>
                          <option value="B.E">B.E</option>
                          <option value="B.Sc">B.Sc</option>
                          <option value="BCA">BCA</option>
                          <option value="BBA">BBA</option>
                          <option value="B.Com">B.Com</option>
                          <option value="BA">BA</option>
                          <option value="M.Tech">M.Tech</option>
                          <option value="M.E">M.E</option>
                          <option value="M.Sc">M.Sc</option>
                          <option value="MCA">MCA</option>
                          <option value="MBA">MBA</option>
                          <option value="M.Com">M.Com</option>
                          <option value="MA">MA</option>
                          <option value="PhD">PhD</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Institution <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={edu.institution}
                          onChange={(e) => {
                            const newEdu = [...newResource.education]
                            newEdu[index].institution = e.target.value
                            setNewResource({...newResource, education: newEdu})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                          placeholder="University/College name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Field of Study
                        </label>
                        <input
                          type="text"
                          value={edu.fieldOfStudy}
                          onChange={(e) => {
                            const newEdu = [...newResource.education]
                            newEdu[index].fieldOfStudy = e.target.value
                            setNewResource({...newResource, education: newEdu})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                          placeholder="e.g., Computer Science"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Year of Passing
                        </label>
                        <input
                          type="number"
                          value={edu.yearOfPassing}
                          onChange={(e) => {
                            const newEdu = [...newResource.education]
                            newEdu[index].yearOfPassing = e.target.value
                            setNewResource({...newResource, education: newEdu})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                          placeholder="e.g., 2020"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Percentage/CGPA
                        </label>
                        <input
                          type="text"
                          value={edu.percentage}
                          onChange={(e) => {
                            const newEdu = [...newResource.education]
                            newEdu[index].percentage = e.target.value
                            setNewResource({...newResource, education: newEdu})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                          placeholder="e.g., 85% or 8.5"
                        />
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={edu.isHighest}
                          onChange={(e) => {
                            const newEdu = [...newResource.education]
                            newEdu[index].isHighest = e.target.checked
                            setNewResource({...newResource, education: newEdu})
                          }}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                          id={`highest-${index}`}
                        />
                        <label htmlFor={`highest-${index}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          Mark as Highest Qualification
                        </label>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Education Document (Certificate/Degree)
                        </label>
                        <div className="flex items-center space-x-4">
                          {edu.documentUrl ? (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <FileText className="w-5 h-5" />
                              <span>{edu.documentName || 'Document uploaded'}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newEdu = [...newResource.education]
                                  newEdu[index].document = null
                                  newEdu[index].documentUrl = ''
                                  newEdu[index].documentName = ''
                                  setNewResource({...newResource, education: newEdu})
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">No document uploaded</div>
                          )}
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files[0]
                              if (file) {
                                const reader = new FileReader()
                                reader.onloadend = () => {
                                  const newEdu = [...newResource.education]
                                  newEdu[index].document = file
                                  newEdu[index].documentUrl = reader.result
                                  newEdu[index].documentName = file.name
                                  setNewResource({...newResource, education: newEdu})
                                }
                                reader.readAsDataURL(file)
                              }
                            }}
                            className="hidden"
                            id={`edu-doc-${index}`}
                          />
                          <label
                            htmlFor={`edu-doc-${index}`}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer inline-flex items-center text-sm transition-all"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {edu.documentUrl ? 'Change Document' : 'Upload Document'}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 4: Professional Experience */}
        {currentStep === 4 && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6 pb-3 border-t border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Building2 className="w-4 h-4 mr-2" />
                  Professional Experience
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    setNewResource({
                      ...newResource,
                      experience: [...newResource.experience, { 
                        company: '', 
                        designation: '', 
                        startDate: '', 
                        endDate: '', 
                        isCurrent: false, 
                        description: '', 
                        technologies: [], 
                        offerLetter: null,
                        offerLetterUrl: '',
                        offerLetterName: '',
                        relievingLetter: null,
                        relievingLetterUrl: '',
                        relievingLetterName: '',
                        payslip1: null,
                        payslip1Url: '',
                        payslip1Name: '',
                        payslip2: null,
                        payslip2Url: '',
                        payslip2Name: '',
                        payslip3: null,
                        payslip3Url: '',
                        payslip3Name: ''
                      }]
                    })
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center text-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Experience
                </button>
              </div>
              <div className="space-y-6">
                {newResource.experience.map((exp, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="font-semibold text-gray-900 dark:text-white">Experience {index + 1}</h5>
                      {newResource.experience.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newExp = newResource.experience.filter((_, i) => i !== index)
                            setNewResource({...newResource, experience: newExp})
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Company Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={exp.company}
                          onChange={(e) => {
                            const newExp = [...newResource.experience]
                            newExp[index].company = e.target.value
                            setNewResource({...newResource, experience: newExp})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Company name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Designation <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={exp.designation}
                          onChange={(e) => {
                            const newExp = [...newResource.experience]
                            newExp[index].designation = e.target.value
                            setNewResource({...newResource, experience: newExp})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Job title"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <DatePicker
                          value={exp.startDate}
                          onChange={(date) => {
                            const newExp = [...newResource.experience]
                            newExp[index].startDate = date
                            setNewResource({...newResource, experience: newExp})
                          }}
                          placeholder="Select start date"
                          maxDate={exp.endDate || new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          End Date
                        </label>
                        <DatePicker
                          value={exp.endDate}
                          onChange={(date) => {
                            const newExp = [...newResource.experience]
                            newExp[index].endDate = date
                            setNewResource({...newResource, experience: newExp})
                          }}
                          placeholder="Select end date"
                          minDate={exp.startDate || undefined}
                          maxDate={new Date().toISOString().split('T')[0]}
                          className={exp.isCurrent ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                        {exp.isCurrent && (
                          <p className="text-xs text-gray-500 mt-1">Currently working here</p>
                        )}
                      </div>
                      <div className="md:col-span-2 flex items-center">
                        <input
                          type="checkbox"
                          checked={exp.isCurrent}
                          onChange={(e) => {
                            const newExp = [...newResource.experience]
                            newExp[index].isCurrent = e.target.checked
                            if (e.target.checked) {
                              newExp[index].endDate = ''
                            }
                            setNewResource({...newResource, experience: newExp})
                          }}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                          id={`current-${index}`}
                        />
                        <label htmlFor={`current-${index}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                          I currently work here
                        </label>
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Job Description
                        </label>
                        <textarea
                          value={exp.description}
                          onChange={(e) => {
                            const newExp = [...newResource.experience]
                            newExp[index].description = e.target.value
                            setNewResource({...newResource, experience: newExp})
                          }}
                          rows={3}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="Describe your role and responsibilities..."
                        />
                      </div>
                      
                      {/* Document Uploads Section */}
                      <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h6 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Supporting Documents</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          
                          {/* Offer Letter */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                              Offer Letter
                            </label>
                            <div className="space-y-2">
                              {exp.offerLetterUrl && (
                                <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <FileCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  <span className="text-xs text-green-700 dark:text-green-300 flex-1 truncate">
                                    {exp.offerLetterName || 'Offer letter uploaded'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].offerLetter = null
                                      newExp[index].offerLetterUrl = ''
                                      newExp[index].offerLetterName = ''
                                      setNewResource({...newResource, experience: newExp})
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0]
                                  if (file) {
                                    const reader = new FileReader()
                                    reader.onloadend = () => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].offerLetter = file
                                      newExp[index].offerLetterUrl = reader.result
                                      newExp[index].offerLetterName = file.name
                                      setNewResource({...newResource, experience: newExp})
                                    }
                                    reader.readAsDataURL(file)
                                  }
                                }}
                                className="hidden"
                                id={`offer-letter-${index}`}
                              />
                              <label
                                htmlFor={`offer-letter-${index}`}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer inline-flex items-center justify-center text-xs transition-all"
                              >
                                <Upload className="w-3 h-3 mr-2" />
                                {exp.offerLetterUrl ? 'Change' : 'Upload'}
                              </label>
                            </div>
                          </div>

                          {/* Relieving Letter */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                              Relieving Letter
                            </label>
                            <div className="space-y-2">
                              {exp.relievingLetterUrl && (
                                <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <FileCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  <span className="text-xs text-green-700 dark:text-green-300 flex-1 truncate">
                                    {exp.relievingLetterName || 'Relieving letter uploaded'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].relievingLetter = null
                                      newExp[index].relievingLetterUrl = ''
                                      newExp[index].relievingLetterName = ''
                                      setNewResource({...newResource, experience: newExp})
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0]
                                  if (file) {
                                    const reader = new FileReader()
                                    reader.onloadend = () => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].relievingLetter = file
                                      newExp[index].relievingLetterUrl = reader.result
                                      newExp[index].relievingLetterName = file.name
                                      setNewResource({...newResource, experience: newExp})
                                    }
                                    reader.readAsDataURL(file)
                                  }
                                }}
                                className="hidden"
                                id={`relieving-letter-${index}`}
                              />
                              <label
                                htmlFor={`relieving-letter-${index}`}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer inline-flex items-center justify-center text-xs transition-all"
                              >
                                <Upload className="w-3 h-3 mr-2" />
                                {exp.relievingLetterUrl ? 'Change' : 'Upload'}
                              </label>
                            </div>
                          </div>

                          {/* Payslip 1 */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                              Last Payslip (Month 1)
                            </label>
                            <div className="space-y-2">
                              {exp.payslip1Url && (
                                <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <FileCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  <span className="text-xs text-green-700 dark:text-green-300 flex-1 truncate">
                                    {exp.payslip1Name || 'Payslip uploaded'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].payslip1 = null
                                      newExp[index].payslip1Url = ''
                                      newExp[index].payslip1Name = ''
                                      setNewResource({...newResource, experience: newExp})
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0]
                                  if (file) {
                                    const reader = new FileReader()
                                    reader.onloadend = () => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].payslip1 = file
                                      newExp[index].payslip1Url = reader.result
                                      newExp[index].payslip1Name = file.name
                                      setNewResource({...newResource, experience: newExp})
                                    }
                                    reader.readAsDataURL(file)
                                  }
                                }}
                                className="hidden"
                                id={`payslip1-${index}`}
                              />
                              <label
                                htmlFor={`payslip1-${index}`}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer inline-flex items-center justify-center text-xs transition-all"
                              >
                                <Upload className="w-3 h-3 mr-2" />
                                {exp.payslip1Url ? 'Change' : 'Upload'}
                              </label>
                            </div>
                          </div>

                          {/* Payslip 2 */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                              Last Payslip (Month 2)
                            </label>
                            <div className="space-y-2">
                              {exp.payslip2Url && (
                                <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <FileCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  <span className="text-xs text-green-700 dark:text-green-300 flex-1 truncate">
                                    {exp.payslip2Name || 'Payslip uploaded'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].payslip2 = null
                                      newExp[index].payslip2Url = ''
                                      newExp[index].payslip2Name = ''
                                      setNewResource({...newResource, experience: newExp})
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0]
                                  if (file) {
                                    const reader = new FileReader()
                                    reader.onloadend = () => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].payslip2 = file
                                      newExp[index].payslip2Url = reader.result
                                      newExp[index].payslip2Name = file.name
                                      setNewResource({...newResource, experience: newExp})
                                    }
                                    reader.readAsDataURL(file)
                                  }
                                }}
                                className="hidden"
                                id={`payslip2-${index}`}
                              />
                              <label
                                htmlFor={`payslip2-${index}`}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer inline-flex items-center justify-center text-xs transition-all"
                              >
                                <Upload className="w-3 h-3 mr-2" />
                                {exp.payslip2Url ? 'Change' : 'Upload'}
                              </label>
                            </div>
                          </div>

                          {/* Payslip 3 */}
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                              Last Payslip (Month 3)
                            </label>
                            <div className="space-y-2">
                              {exp.payslip3Url && (
                                <div className="flex items-center space-x-2 px-3 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                  <FileCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                                  <span className="text-xs text-green-700 dark:text-green-300 flex-1 truncate">
                                    {exp.payslip3Name || 'Payslip uploaded'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].payslip3 = null
                                      newExp[index].payslip3Url = ''
                                      newExp[index].payslip3Name = ''
                                      setNewResource({...newResource, experience: newExp})
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files[0]
                                  if (file) {
                                    const reader = new FileReader()
                                    reader.onloadend = () => {
                                      const newExp = [...newResource.experience]
                                      newExp[index].payslip3 = file
                                      newExp[index].payslip3Url = reader.result
                                      newExp[index].payslip3Name = file.name
                                      setNewResource({...newResource, experience: newExp})
                                    }
                                    reader.readAsDataURL(file)
                                  }
                                }}
                                className="hidden"
                                id={`payslip3-${index}`}
                              />
                              <label
                                htmlFor={`payslip3-${index}`}
                                className="w-full px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer inline-flex items-center justify-center text-xs transition-all"
                              >
                                <Upload className="w-3 h-3 mr-2" />
                                {exp.payslip3Url ? 'Change' : 'Upload'}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

  {/* Step 5: Documents */}
  {currentStep === 5 && (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900/50 dark:to-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
              <div className="flex items-center justify-between mb-6 pb-3 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                  <Award className="w-4 h-4 mr-2" />
                  Certifications
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    setNewResource({
                      ...newResource,
                      certifications: [...(newResource.certifications || []), { name: '', issuer: '', issueDate: '', expiryDate: '', credentialId: '', credentialUrl: '', document: null, documentUrl: '', documentName: '' }]
                    })
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center text-sm transition-all"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Certification
                </button>
              </div>
              <div className="space-y-4 mb-4">
                {(newResource.certifications || []).map((cert, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-4">
                      <h5 className="font-semibold text-gray-900 dark:text-white">Certification {index + 1}</h5>
                      {(newResource.certifications || []).length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newCerts = (newResource.certifications || []).filter((_, i) => i !== index)
                            setNewResource({...newResource, certifications: newCerts})
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Certification Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={cert.name}
                          onChange={(e) => {
                            const newCerts = [...(newResource.certifications || [])]
                            newCerts[index].name = e.target.value
                            setNewResource({...newResource, certifications: newCerts})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="e.g., AWS Certified Solutions Architect"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Issuing Organization
                        </label>
                        <input
                          type="text"
                          value={cert.issuer}
                          onChange={(e) => {
                            const newCerts = [...(newResource.certifications || [])]
                            newCerts[index].issuer = e.target.value
                            setNewResource({...newResource, certifications: newCerts})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="e.g., Amazon Web Services"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Issue Date
                        </label>
                        <DatePicker
                          value={cert.issueDate}
                          onChange={(date) => {
                            const newCerts = [...(newResource.certifications || [])]
                            newCerts[index].issueDate = date
                            setNewResource({...newResource, certifications: newCerts})
                          }}
                          placeholder="Select issue date"
                          maxDate={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Expiry Date
                        </label>
                        <DatePicker
                          value={cert.expiryDate}
                          onChange={(date) => {
                            const newCerts = [...(newResource.certifications || [])]
                            newCerts[index].expiryDate = date
                            setNewResource({...newResource, certifications: newCerts})
                          }}
                          placeholder="Select expiry date (if applicable)"
                          minDate={cert.issueDate || undefined}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Credential ID
                        </label>
                        <input
                          type="text"
                          value={cert.credentialId}
                          onChange={(e) => {
                            const newCerts = [...(newResource.certifications || [])]
                            newCerts[index].credentialId = e.target.value
                            setNewResource({...newResource, certifications: newCerts})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="Credential ID or License number"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Credential URL
                        </label>
                        <input
                          type="url"
                          value={cert.credentialUrl}
                          onChange={(e) => {
                            const newCerts = [...(newResource.certifications || [])]
                            newCerts[index].credentialUrl = e.target.value
                            setNewResource({...newResource, certifications: newCerts})
                          }}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white transition-all"
                          placeholder="Verification URL"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                          Certification Document
                        </label>
                        <div className="flex items-center space-x-4">
                          {cert.documentUrl ? (
                            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                              <FileText className="w-5 h-5" />
                              <span>{cert.documentName || 'Document uploaded'}</span>
                              <button
                                type="button"
                                onClick={() => {
                                  const newCerts = [...(newResource.certifications || [])]
                                  newCerts[index].document = null
                                  newCerts[index].documentUrl = ''
                                  newCerts[index].documentName = ''
                                  setNewResource({...newResource, certifications: newCerts})
                                }}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-500">No document uploaded</div>
                          )}
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files[0]
                              if (file) {
                                const reader = new FileReader()
                                reader.onloadend = () => {
                                  const newCerts = [...(newResource.certifications || [])]
                                  newCerts[index].document = file
                                  newCerts[index].documentUrl = reader.result
                                  newCerts[index].documentName = file.name
                                  setNewResource({...newResource, certifications: newCerts})
                                }
                                reader.readAsDataURL(file)
                              }
                            }}
                            className="hidden"
                            id={`cert-doc-${index}`}
                          />
                          <label
                            htmlFor={`cert-doc-${index}`}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 cursor-pointer inline-flex items-center text-sm transition-all"
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {cert.documentUrl ? 'Change Document' : 'Upload Document'}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </motion.div>
        )}


  

        {/* Action Buttons */}
        <div className={`border-t border-gray-200 dark:border-gray-700 ${isModal ? 'mt-3 pt-3' : 'mt-4 pt-4'} flex justify-between`}>
          <button
            type="button"
            onClick={currentStep === 1 
              ? () => {
                  // Clear localStorage on cancel
                  localStorage.removeItem('newResourceForm')
                  if (isModal && onClose) {
                    onClose()
                  } else {
                    navigate('/vendor/dashboard/bench-resources')
                  }
                }
              : handlePrevious}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium flex items-center"
            disabled={addingResource}
          >
            <ChevronLeft className="w-4 h-4 mr-1.5" />
            {currentStep === 1 ? 'Cancel' : 'Previous'}
          </button>
          <button
            type="submit"
            className="px-5 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center font-semibold shadow-sm"
            disabled={addingResource}
          >
            {addingResource ? (
              <>
                <Loader className="w-4 h-4 mr-1.5 animate-spin" />
                {currentStep === 1 && !step1Submitted ? 'Saving...' : 'Saving...'}
              </>
            ) : currentStep === totalSteps ? (
              <>
                <CheckCircle className="w-4 h-4 mr-1.5" />
                {isStepDirty() ? 'Save Changes' : 'Submit & Add Resource'}
              </>
            ) : (isStepDirty() || (currentStep === 1 && !step1Submitted)) ? (
              <>
                Save
                <ChevronRight className="w-4 h-4 ml-1.5" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4 ml-1.5" />
              </>
            )}
          </button>
        </div>
      </form>
      {/* live recording modal removed - self-intro upload only */}
    </div>
  )
}

export default AddBenchResource

